import { Injectable, inject } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { AuthenticationService } from "../services/authentication.service";
import { LoggerService } from "../services/logger.service";
import Swal from "sweetalert2";
import { TenantService } from "../services/tenant.service";
import { MetricCollectionService } from "../services/metric-collection.service";
import { environment } from "../../../environments/environment";

const DISABLED_CSPS_TRIAL = ["azure", "gcp", "oci", "drcc", "evo"];

@Injectable()
export class AuthGuard {
  private log = inject(LoggerService);
  private authN = inject(AuthenticationService);
  private metrics = inject(MetricCollectionService);
  private tenant = inject(TenantService);
  private router = inject(Router);


  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    if (localStorage.getItem("runningCypressTest")) {
      return true;
    }

    const isLoggedIn = this.authN.isLoggedIn();

    if (isLoggedIn) {
      const url = route.pathFromRoot.map((v) => v.url.map((segment) => segment.toString()).join("/")).join("/");
      this.metrics.reportPageVisited(url);
      this.log.info({ route, state });
      if (this.authN.tenant === "trial") {
        const isRestricted: boolean = route.data["disallowTrial"] || route.parent?.data["disallowTrial"];
        this.log.info("trial user");
        if (isRestricted) {
          this.log.info("Access denied: Trial user on restricted route");
          return this.router.parseUrl("/dashboard");
        }
        for (const csp of DISABLED_CSPS_TRIAL) {
          for (const s of [state.url || "", route.fragment || ""]) {
            if (s.includes(`/${csp}/`) || s.endsWith(`/${csp}`) || s.startsWith(`${csp}/`) || s === csp) {
              return false;
            }
          }
        }
      }
      if ((state?.url ?? "").includes("vcloudsmart")) {
        try {
          this.saveRecentlyUsedVCSReport(state.url);
        } catch (e) {
          this.log.error(e);
        }
      }
      return isLoggedIn;
    }

    const email = this.authN.email;

    this.log.info({ route, state, email });

    this.authN.saveUrlForLater();

    await Swal.fire({
      icon: "warning",
      title: "Session Expired",
      text: "Click on the button below to re-authenticate.",
      confirmButtonColor: this.tenant.colors.primary,
      allowOutsideClick: false,
      confirmButtonText: "Reauthenticate",
      footer: "",
    });

    if (environment.local && location.hostname === "localhost") {
      this.authN.onLoginClickCognitoV2SignIn();
    } else {
      this.authN.onLoginClickAAD();
    }

    return false;
  }

  private saveRecentlyUsedVCSReport(link: string, maxReports = 3): void {
    const [csp, section, page] = link
      .split("/")
      .filter(Boolean)
      .filter((s) => s !== "vcloudsmart" && s !== "v2");
    let existingPages: { title: string; section: string; csp: string }[] = [];
    const maybePages: string = localStorage.getItem(`recentlyVisited/${csp}`);
    if (maybePages) {
      const parsed: { title: string; section: string; csp: string }[] = JSON.parse(maybePages);
      if (Array.isArray(parsed)) {
        existingPages = parsed;
      }
    }
    const newPages: { title: string; section: string; csp: string }[] = [
      { title: page, section: section, csp: csp },
      ...existingPages.filter((p) => {
        return !(p?.title === page && p?.section === section && p?.csp === csp);
      }),
    ].slice(0, maxReports);
    localStorage.setItem(`recentlyVisited/${csp}`, JSON.stringify(newPages));
  }
}
